'use client';

import { Fragment } from 'react';
import { ListboxOption } from '@headlessui/react';

import { stateAbbreviationTitles } from '@/constants/states';

import { PropsWithClasses } from '@lamesarv-sdk/types';
import { IDynamicsOptionSet } from '@/types/dynamicsForms';

import { CommonSelect } from './CommonSelect';

interface IStateGroupProps {
  stateAbbreviation: string;
  locations: {
    city: string;
    fullLocation: string;
  }[];
}

const StateGroup = (props: IStateGroupProps) => (
  <div className="py-1" role="none" key={props.stateAbbreviation}>
    <label
      className="block px-4 py-2 text-sm font-bold uppercase text-gray-900"
      htmlFor={stateAbbreviationTitles[props.stateAbbreviation]}
    >
      {stateAbbreviationTitles[props.stateAbbreviation]}
    </label>
    {props.locations.map((location) => (
      <ListboxOption
        className="px-4 py-2 text-sm text-black-500 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
        value={location.fullLocation}
        key={location.city}
      >
        {location.city}
      </ListboxOption>
    ))}
  </div>
);

interface ICommonLocationSelectProps extends PropsWithClasses {
  locationsOptionSet: IDynamicsOptionSet;
  name?: string;
  label?: string;
  hideLabel?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  classContainer?: string;
  classLabel?: string;
  classLabelWithValue?: string;
  classInputWithValue?: string;
}

export const CommonLocationSelect = (props: ICommonLocationSelectProps) => {
  if (!props.locationsOptionSet) return null;

  const stateLocations = props.locationsOptionSet.reduce(
    (acc, option) => {
      const [city, stateAbbreviation] = option.Label.split(',').map((part) => part.trim());
      if (!stateAbbreviation || !stateAbbreviationTitles[stateAbbreviation]) return acc;
      if (!acc[stateAbbreviation]) acc[stateAbbreviation] = [];
      acc[stateAbbreviation].push({ city, fullLocation: option.Label });
      return acc;
    },
    {} as Record<
      string,
      {
        city: string;
        fullLocation: string;
      }[]
    >,
  );

  const getOptionsDesktop = () => {
    return (
      <Fragment>
        {Object.entries(stateLocations)
          .sort(([stateAbbreviationA], [stateAbbreviationB]) =>
            stateAbbreviationTitles[stateAbbreviationA].localeCompare(stateAbbreviationTitles[stateAbbreviationB]),
          )
          .map(([stateAbbreviation, locations]) => (
            <StateGroup key={stateAbbreviation} stateAbbreviation={stateAbbreviation} locations={locations} />
          ))}
      </Fragment>
    );
  };

  const getOptionsMobile = () => {
    return (
      <Fragment>
        {Object.entries(stateLocations)
          .sort(([stateAbbreviationA], [stateAbbreviationB]) =>
            stateAbbreviationTitles[stateAbbreviationA].localeCompare(stateAbbreviationTitles[stateAbbreviationB]),
          )
          .map(([stateAbbreviation, locations]) => (
            <optgroup key={stateAbbreviation} label={stateAbbreviationTitles[stateAbbreviation]}>
              {locations.map((location) => (
                <option key={location.city} value={location.fullLocation}>
                  {location.city}
                </option>
              ))}
            </optgroup>
          ))}
      </Fragment>
    );
  };

  return (
    <CommonSelect
      {...props}
      name="location"
      options={[]}
      optionsCustomDesktop={getOptionsDesktop()}
      optionsCustomMobile={getOptionsMobile()}
      useValueToDisplay
    />
  );
};
